// Walks the matched-route handles (set on each route definition) and updates document.title.
// Replaces the Angular AppComponent NavigationEnd subscription that uses `data.title`/`subtitle`/`tab`.
import { useEffect } from 'react';
import { useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface RouteHandle {
  title?: string;
  subtitle?: string;
  tab?: string;
  tabTranslatePath?: string;
  permissions?: string[];
}

export function useRouteTitle(): void {
  const matches = useMatches();
  const { t } = useTranslation();

  useEffect(() => {
    let title = '';
    let subtitle = '';
    let tab = '';
    let tabTranslatePath: string | undefined;

    for (const m of matches) {
      const h = (m.handle ?? {}) as RouteHandle;
      if (h.title) title = h.title;
      if (h.subtitle) subtitle = h.subtitle;
      if (h.tab) tab = h.tab;
      if (h.tabTranslatePath) tabTranslatePath = h.tabTranslatePath;
    }

    const lastMatch = matches[matches.length - 1];
    const paramTab = (lastMatch?.params as Record<string, string | undefined>)?.tab;
    if (paramTab && !tab) tab = paramTab;

    const titleText = title ? t(title, { defaultValue: title }) : '';
    const subtitleText = subtitle ? t(subtitle, { defaultValue: subtitle }) : '';
    const tabKey = tabTranslatePath && tab ? `${tabTranslatePath}.${tab}` : tab;
    const tabText = tabKey ? t(tabKey, { defaultValue: tabKey }) : '';

    document.title = `Diool Ops Portal - ${titleText}${
      subtitleText ? ' - ' + subtitleText : ''
    }${tabText ? ' - ' + tabText : ''}`;
  }, [matches, t]);
}

// Convenience: pull the merged handle for the deepest match.
export function useCurrentRouteHandle(): RouteHandle {
  const matches = useMatches();
  return matches.reduce<RouteHandle>((acc, m) => {
    const h = (m.handle ?? {}) as RouteHandle;
    return { ...acc, ...h };
  }, {});
}
